/**
 * Color System — Athletic Lime + Charcoal (2026-05-19)
 * ─────────────────────────────────────────────────────────────
 *
 * Eine eigenständige Performance-Marke: Slate-Charcoal als Fundament,
 * warmes Creme im Light, electric Lime als Brand-Akzent für ALLE
 * Modi. Inspiriert von Hoka/Whoop/Lululemon — premium-athletic,
 * keine generische Fitness-Optik.
 *
 * Drei Modi liefern subtile Kontext-Shifts, behalten aber die EINE
 * Marken-Identität — die Lime/Cream/Slate-Achse zieht sich durch:
 *
 *   neutral → Home, Settings           (cream base, lime accent)
 *   warm    → Food                     (leicht wärmeres cream)
 *   dark    → Training                 (slate, lime pops)
 *
 * Theme switches animate between these palettes at runtime.
 * Siehe ThemeProvider für Transition-Logic.
 */

/** Complete color palette shape. Every theme must satisfy this. */
export interface ColorPalette {
  // Backgrounds
  bg: string;           // Page background
  surface: string;      // Card surface
  surfaceAlt: string;   // Subtle surface variant (secondary buttons, chips)
  white: string;        // Pure modal/overlay background

  // Text
  text: string;         // Primary text
  textDim: string;      // Secondary/meta text
  textMuted: string;    // Tertiary text (hints, placeholders)

  // Accent (primary action — Lime, brand-konsistent über alle Modi)
  accent: string;
  accentText: string;   // Dark text on lime
  accentSoft: string;   // Lime wash for backgrounds/chips
  // Accent als VORDERGRUND (Text/Icon auf der Seite/Card). Dark: Lime (lesbar
  // auf Slate). Light: Slate — Lime ist auf Cream/Weiß nicht lesbar (~2:1), im
  // Light Mode wird Akzent-Text daher dunkel gerendert (Lime bleibt Füllung).
  accentInk: string;

  // Semantic — stay roughly consistent across modes to preserve meaning.
  success: string;
  successSoft: string;
  info: string;
  infoSoft: string;
  warning: string;
  warningSoft: string;
  danger: string;
  dangerSoft: string;
  purple: string;       // Charts / Mikronährstoffe

  // Borders
  border: string;
  borderStrong: string;

  // Shadows (mode-specific — 'none' for dark)
  shadow: string;
}

/** The three contextual modes. */
export type ThemeMode = 'neutral' | 'warm' | 'dark';

// ─── Brand Constants (constant across modes) ───────────────
// Brand-Akzent (entgrellt) — Source of Truth: brand/athlyn-stil.md.
// ⚠️ KEIN Neon: das alte #B5E832 / #C5F84A war zu grell und ist verworfen.
const LIME = '#9BC22E';               // Marken-Akzent
const LIME_ON_DARK = '#9BC22E';       // auf Slate #0A0D12 ≈ 9,5:1 (WCAG AA), kein Neon-Lift nötig
const SLATE = '#0A0D12';
const SLATE_LIFTED = '#11151C';
const SLATE_ELEVATED = '#1A1F28';

/** Full palettes for every mode. */
export const palettes: Record<ThemeMode, ColorPalette> = {
  // ─── NEUTRAL: Home, Settings, Onboarding (Cream Light) ───
  neutral: {
    bg: '#F5F2EB',         // warm cream
    surface: '#FFFFFF',
    surfaceAlt: '#EDE9DE',
    white: '#FFFFFF',

    text: SLATE,
    textDim: '#5A5F66',
    textMuted: '#6B7280',    // dunkler für WCAG-AA auf Cream (#9098A0 war ~2,9:1)

    accent: LIME,
    accentText: SLATE,       // dark text on lime = high contrast pop
    accentSoft: '#E5F9B5',   // lime wash, an satteren Ton angepasst
    accentInk: SLATE,        // Akzent-Text im Light Mode = Slate (lesbar)

    success: '#16A34A',
    successSoft: '#F0FDF4',
    info: '#2563EB',
    infoSoft: '#EFF6FF',
    warning: '#D97706',
    warningSoft: '#FFFBEB',
    danger: '#DC2626',
    dangerSoft: '#FEF2F2',
    purple: '#7C3AED',

    border: '#E0DCD0',
    borderStrong: '#C4BFAF',
    shadow: '0 2px 14px rgba(10, 13, 18, 0.05)',
  },

  // ─── WARM: Food (leicht wärmeres Cream, gleicher Lime-Accent) ───
  warm: {
    bg: '#F8F4ED',
    surface: '#FFFFFF',
    surfaceAlt: '#F0EAE0',
    white: '#FFFFFF',

    text: SLATE,
    textDim: '#5A5F66',
    textMuted: '#6B7280',    // dunkler für WCAG-AA auf Cream

    accent: LIME,
    accentText: SLATE,
    accentSoft: '#E5F9B5',
    accentInk: SLATE,        // Akzent-Text im Light Mode = Slate (lesbar)

    success: '#16A34A',
    successSoft: '#F0FDF4',
    info: '#2563EB',
    infoSoft: '#EFF6FF',
    warning: '#D97706',
    warningSoft: '#FFFBEB',
    danger: '#DC2626',
    dangerSoft: '#FEF2F2',
    purple: '#7C3AED',

    border: '#E5E0D3',
    borderStrong: '#C8C2B0',
    shadow: '0 2px 14px rgba(10, 13, 18, 0.05)',
  },

  // ─── DARK: Training (Slate, Lime peakt) ───
  dark: {
    bg: SLATE,
    surface: SLATE_LIFTED,
    surfaceAlt: SLATE_ELEVATED,
    white: SLATE_LIFTED,

    text: '#FFFFFF',
    textDim: '#A6ACB5',
    textMuted: '#7A828C',

    accent: LIME_ON_DARK,
    accentText: SLATE,
    accentSoft: '#1B240A',
    accentInk: LIME_ON_DARK,  // Dark: Lime als Text ist auf Slate lesbar (~9,5:1)

    success: '#65D928',
    successSoft: '#1A2E0A',
    info: '#60A5FA',
    infoSoft: '#0C1B2E',
    warning: '#FBBF24',
    warningSoft: '#2E2308',
    danger: '#F87171',
    dangerSoft: '#3E1414',
    purple: '#A78BFA',

    border: '#1F252F',
    borderStrong: '#2E3640',
    shadow: 'none',
  },
};

/**
 * Maps a tab key to the theme mode it should trigger.
 * 2026-06: App-weit auf Dark-Premium (Whoop-Stil) vereinheitlicht — eine
 * konsistente dunkle Identität statt Cream/Slate-Wechsel pro Tab.
 */
export const tabToMode: Record<string, ThemeMode> = {
  home: 'dark',
  food: 'dark',
  training: 'dark',
  settings: 'dark',
  camera: 'dark',
};
